import {
  escapeRegExp,
  isFinite,
  isNil,
  isPlainObject,
  groupBy as lodashGroupBy,
  isString as lodashIsString,
  keys as lodashKeys,
  map as lodashMap,
  omit as lodashOmit,
  noop,
  pickBy,
  replace,
  toNumber,
} from 'lodash-es';
import { P, match } from '../match';
import type { StrictOmit } from '../types';

export const keys = <T extends GenericRecord>(obj: T): (keyof T)[] => lodashKeys(obj);

export const isString = <T extends string = string>(s: T | unknown): s is T => lodashIsString(s);

export const replaceAll = (string: string, pattern: string, replacement: string): string =>
  pattern === '' ? string : replace(string, new RegExp(escapeRegExp(pattern), 'g'), replacement);

export const curriedReplaceAll = (search: string, replace: string) => (string: string) =>
  replaceAll(string, search, replace);

export const isNotNil = <T>(val: T) => !isNil(val);

/** Immutable reverse */
// eslint-disable-next-line fp/no-mutating-methods
export const reverse = <T>(array: T[]) => array.slice().reverse();

export const omit = <T extends GenericRecord, K extends keyof T>(obj: MaybeUndefined<T>, keys: K[]) =>
  lodashOmit(obj, keys) as StrictOmit<T, K>;

/**
 * @description Removes keys from object that have the following values: "" | undefined | null
 */
export const removeNullishAndEmptyStringValues = <T extends Record<string, unknown>>(obj: T): T =>
  pickBy(obj, (value) => value !== '' && !isNil(value)) as T;

const isPossibleNumber = (value: string) => isFinite(toNumber(value));

export const safeParseInt = (stringNum: number | string, fallback: number = 0): number =>
  match(stringNum)
    .with(P.union(NaN, 'NaN'), () => 0)
    .with(P.number, (val) => val)
    .with('-', () => 0)
    .with(P.when(isPossibleNumber), (val) => parseInt(val))
    .otherwise(() => fallback);

export const extractReadonlyValues = <T extends Readonly<GenericRecord>, K extends keyof T>(
  array: Readonly<T[]>,
  key: K,
): T[K][] => lodashMap(array, key);

export const getRandomElementsFromArray = <T>(arr: T[], n: number): T[] => {
  // eslint-disable-next-line fp/no-let, prefer-const
  let copy = [...arr];
  return Array.from({ length: Math.min(n, arr.length) }).map(() => {
    const randIndex = Math.floor(Math.random() * copy.length);
    // eslint-disable-next-line fp/no-mutating-methods
    return copy.splice(randIndex, 1)[0];
  }) as T[];
};

export const safeStringConcat = (...strings: MaybeNil<string>[]) => strings.map((str) => str ?? '').join('');

export const filterNullish = <T>(array: T[]): NonNullable<T>[] =>
  array.filter((item) => item !== null && item !== undefined) as NonNullable<T>[];

export const coalesceToUndefined = <T>(value: T) =>
  (value ?? undefined) as T extends Nil ? MaybeUndefined<NonNullable<T>> : T;

/**
 * Deep-merges a potentially partial or malformed source into a complete defaults
 * object. Only keys present in defaults are kept. Nested plain objects are recursed;
 * primitives and arrays in source override defaults when their type matches.
 * Returns defaults unchanged if source is not a plain object.
 */
export const deepMergeDefaults = <T extends Record<string, unknown>>(defaults: T, source: unknown): T => {
  if (!isPlainObject(source)) return defaults;
  const sourceRecord = source as Record<string, unknown>;
  return Object.fromEntries(
    Object.entries(defaults).map(([key, defaultValue]) => {
      const sourceValue = sourceRecord[key];

      if (isPlainObject(defaultValue) && isPlainObject(sourceValue)) {
        return [key, deepMergeDefaults(defaultValue as Record<string, unknown>, sourceValue)];
      }

      if (Array.isArray(defaultValue)) {
        return [key, Array.isArray(sourceValue) ? sourceValue : defaultValue];
      }

      return [
        key,
        sourceValue !== undefined && sourceValue !== null && typeof sourceValue === typeof defaultValue
          ? sourceValue
          : defaultValue,
      ];
    }),
  ) as T;
};

export {
  camelCase,
  capitalize,
  chunk,
  concat,
  debounce,
  entries,
  filter,
  identity,
  includes,
  isArray,
  isEmpty,
  isEqual,
  isFinite,
  isFunction,
  isNumber,
  isObject,
  isPlainObject,
  kebabCase,
  last,
  maxBy,
  merge,
  orderBy,
  partition,
  pick,
  reduce,
  remove,
  snakeCase,
  some,
  sortBy,
  startCase,
  sumBy,
  toLower,
  toNumber,
  toUpper,
  trim,
  uniqBy,
  upperCase,
  values,
} from 'lodash-es';

export { lodashGroupBy as groupBy, isNil, noop };
